import React, { useState, useEffect, useMemo } from 'react';
import {
  Card, Table, Button, Space, Tag, Alert, Modal, message,
  Input, Select, Statistic, Row, Col, Tooltip, Collapse
} from 'antd';
import { MailOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiService } from '../../../../services/apiService';
import invoiceService from '../../../../services/invoiceService';
import { useTranslation } from "react-i18next";

const { Option } = Select;

/**
 * Chasing overdue invoices.
 *
 * Deliberately manual: someone picks who to chase and presses send. There are
 * thousands of overdue invoices on the live system, so an unattended chaser
 * would be a lot of mail going out on nothing but a date comparison.
 *
 * Anything already chased shows when it was last chased, so the same customer
 * is not reminded twice in a day by two different people.
 */
const ARChasing: React.FC = () => {
    const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [reminded, setReminded] = useState<Record<string, { sentAt: string; to: string[] }>>({});
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [ageFilter, setAgeFilter] = useState<string>('all');
  const [result, setResult] = useState<null | {
    sent: Array<{ invoiceNo: string; to: string }>;
    skipped: Array<{ invoiceNo: string; customer: string }>;
    failed: Array<{ invoiceNo: string; error: string }>;
  }>(null);
  // The reminders as they will actually be sent. Held here so they can be
  // read — and edited — before anything leaves the building.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [drafts, setDrafts] = useState<Array<{
    id: string; invoiceNo: string; customer: string; to: string; subject: string; body: string;
  }>>([]);
  const [previewSkipped, setPreviewSkipped] = useState<Array<{ invoiceNo: string; customer: string }>>([]);

  const load = async () => {
    setLoading(true);
    try {
      const [overdue, summary] = await Promise.all([
        apiService.get('/accounting/invoices/overdue'),
        apiService.get('/accounting/invoices/reminder-summary'),
      ]);
      const list = (overdue as any)?.data || (Array.isArray(overdue) ? overdue : []) || [];
      setInvoices(Array.isArray(list) ? list : []);
      setReminded(((summary as any)?.data || summary || {}) as any);
    } catch (e: any) {
      message.error(t('Could not load overdue invoices: {{message}}', { message: e.message }));
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const daysOverdue = (inv: any) =>
    inv.dueDate ? Math.max(0, dayjs().startOf('day').diff(dayjs(inv.dueDate).startOf('day'), 'day')) : 0;

  const recipientFor = (inv: any) => inv.contactEmail || inv.customer?.email || '';

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return invoices
      .filter(inv => {
        const d = daysOverdue(inv);
        if (ageFilter === '1-30' && !(d >= 1 && d <= 30)) return false;
        if (ageFilter === '31-60' && !(d >= 31 && d <= 60)) return false;
        if (ageFilter === '61-90' && !(d >= 61 && d <= 90)) return false;
        if (ageFilter === '90+' && d <= 90) return false;
        return true;
      })
      .filter(inv =>
        !q ||
        (inv.invoiceNo || '').toLowerCase().includes(q) ||
        (inv.customer?.name || '').toLowerCase().includes(q)
      )
      .sort((a, b) => daysOverdue(b) - daysOverdue(a));
  }, [invoices, search, ageFilter]);

  const totals = useMemo(() => ({
    count: rows.length,
    amount: rows.reduce((sum, r) => sum + Number(r.totalAmount || 0), 0),
    noEmail: rows.filter(r => !recipientFor(r)).length,
  }), [rows]);

  /**
   * The exact wording that will be sent for one invoice. Kept separate from
   * the sending so the same text can be shown for review first — previously
   * it was built inside the send loop, so nobody ever saw it.
   *
   * NOT translated on purpose — this reminder goes to the customer, so it must
   * not follow whichever locale the operator happens to have selected.
   */
  const composeReminder = (inv: any) => {
    const days = daysOverdue(inv);
    const companyName = inv.company?.name || 'Shoptoplus';
    return {
      id: inv.id,
      invoiceNo: inv.invoiceNo,
      customer: inv.customer?.name || '—',
      to: recipientFor(inv),
      subject: `Payment reminder: invoice ${inv.invoiceNo} is ${days} day${days === 1 ? '' : 's'} overdue`,
      body:
        `Dear ${inv.customer?.name || 'Customer'},\n\n` +
        `Our records show that invoice ${inv.invoiceNo} for ${inv.currency || 'HKD'} ` +
        `${Number(inv.totalAmount || 0).toFixed(2)}, due on ` +
        // .locale('en') for the same reason the wording is not translated: the
        // app sets dayjs's locale globally from the operator's UI language, so
        // without this a Chinese operator sent customers "27 一月 2021".
        `${dayjs(inv.dueDate).locale('en').format('D MMMM YYYY')}, remains unpaid — ` +
        `${days} day${days === 1 ? '' : 's'} past its due date.\n\n` +
        `A copy of the invoice is attached. If payment has already been made, ` +
        `please disregard this message and send us the payment record.\n\n` +
        `Thank you,\n${companyName}`,
    };
  };

  /** Build the drafts and show them. Nothing is sent from here. */
  const openPreview = () => {
    const selected = rows.filter(r => selectedRowKeys.includes(r.id));
    if (!selected.length) return;
    setDrafts(selected.filter(r => recipientFor(r)).map(composeReminder));
    setPreviewSkipped(
      selected.filter(r => !recipientFor(r))
        .map(r => ({ invoiceNo: r.invoiceNo, customer: r.customer?.name || '—' }))
    );
    setPreviewOpen(true);
  };

  const updateDraft = (id: string, field: 'to' | 'subject' | 'body', value: string) =>
    setDrafts(prev => prev.map(d => (d.id === id ? { ...d, [field]: value } : d)));

  /** Sends exactly what is in the drafts — including any edits made above. */
  const sendReminders = async () => {
    if (!drafts.length) { setResult({ sent: [], skipped: previewSkipped, failed: [] }); setPreviewOpen(false); return; }

    setSending(true);
    const sent: Array<{ invoiceNo: string; to: string }> = [];
    const failed: Array<{ invoiceNo: string; error: string }> = [];

    // One at a time — a burst of mail is how you get rate-limited.
    for (const d of drafts) {
      try {
        await invoiceService.sendInvoiceEmail(d.id, {
          to: d.to.split(',').map(x => x.trim()).filter(Boolean),
          subject: d.subject,
          body: d.body,
          kind: 'REMINDER',
        } as any);
        sent.push({ invoiceNo: d.invoiceNo, to: d.to });
      } catch (e: any) {
        failed.push({ invoiceNo: d.invoiceNo, error: e?.message || 'Send failed' });
      }
    }

    setSending(false);
    setPreviewOpen(false);
    setResult({ sent, skipped: previewSkipped, failed });
    setSelectedRowKeys([]);
    setDrafts([]);
    setPreviewSkipped([]);
    load();
  };

  const columns = [
    {
      title: t('Invoice'),
      dataIndex: 'invoiceNo',
      width: 170,
      sorter: (a: any, b: any) => (a.invoiceNo || '').localeCompare(b.invoiceNo || ''),
    },
    {
      title: t('Customer'),
      render: (_: any, r: any) => r.customer?.name || '—',
    },
    {
      title: t('Email'),
      width: 210,
      render: (_: any, r: any) => {
        const to = recipientFor(r);
        return to
          ? <span>{to}</span>
          : <Tag color="orange" icon={<WarningOutlined />}>none on file</Tag>;
      },
    },
    {
      title: t('Due'),
      dataIndex: 'dueDate',
      width: 110,
      render: (d: string) => (d ? dayjs(d).format('YYYY-MM-DD') : '—'),
      sorter: (a: any, b: any) => dayjs(a.dueDate).valueOf() - dayjs(b.dueDate).valueOf(),
    },
    {
      title: t('Overdue'),
      width: 110,
      defaultSortOrder: 'descend' as const,
      sorter: (a: any, b: any) => daysOverdue(a) - daysOverdue(b),
      render: (_: any, r: any) => {
        const d = daysOverdue(r);
        const colour = d > 90 ? 'red' : d > 60 ? 'volcano' : d > 30 ? 'orange' : 'gold';
        return <Tag color={colour}>{d} day{d === 1 ? '' : 's'}</Tag>;
      },
    },
    {
      title: t('Amount'),
      dataIndex: 'totalAmount',
      width: 130,
      align: 'right' as const,
      render: (v: any, r: any) =>
        `${r.currency || 'HKD'} ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      sorter: (a: any, b: any) => Number(a.totalAmount || 0) - Number(b.totalAmount || 0),
    },
    {
      title: t('Last chased'),
      width: 150,
      render: (_: any, r: any) => {
        const last = reminded[r.id];
        if (!last) return <span style={{ color: '#bbb' }}>never</span>;
        const when = dayjs(last.sentAt);
        return (
          <Tooltip title={`Sent to ${(last.to || []).join(', ')}`}>
            <span>{when.format('YYYY-MM-DD')}</span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card><Statistic title={t('Overdue invoices')} value={totals.count} /></Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title={t('Total overdue')} value={totals.amount} precision={2} prefix="HKD" />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title={t('Cannot be chased (no email)')}
              value={totals.noEmail}
              groupSeparator=","
              valueStyle={{ color: totals.noEmail ? '#d46b08' : undefined }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={t('Overdue invoices')}
        extra={
          <Space>
            {selectedRowKeys.length > 0 && (
              <Button type="primary" icon={<MailOutlined />} loading={sending} onClick={openPreview}>
                {t('Preview')} {selectedRowKeys.length} reminder{selectedRowKeys.length > 1 ? 's' : ''}
              </Button>
            )}
            <Button icon={<ReloadOutlined />} onClick={load}>{t('Refresh')}</Button>
          </Space>
        }
      >
        <Space style={{ marginBottom: 16 }} wrap>
          <Input.Search
            allowClear
            placeholder={t('Search by invoice no. or customer')}
            style={{ width: 320 }}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <Select value={ageFilter} onChange={setAgeFilter} style={{ width: 180 }}>
            <Option value="all">{t('All ages')}</Option>
            <Option value="1-30">1–30 days</Option>
            <Option value="31-60">31–60 days</Option>
            <Option value="61-90">61–90 days</Option>
            <Option value="90+">{t('Over 90 days')}</Option>
          </Select>
        </Space>

        {totals.noEmail > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={`${totals.noEmail.toLocaleString()} of these invoices are for customers with no email address on file, and cannot be chased from here.`}
          />
        )}

        <Table
          rowKey="id"
          loading={loading}
          columns={columns as any}
          dataSource={rows}
          rowSelection={{
            selectedRowKeys,
            onChange: keys => setSelectedRowKeys(keys),
            getCheckboxProps: (r: any) => ({ disabled: !recipientFor(r) }),
          }}
          pagination={{ defaultPageSize: 20, showSizeChanger: true, showTotal: t => `${t} overdue` }}
          scroll={{ x: 1100 }}
        />
      </Card>

      {/* Read the mail before it goes out — the same courtesy the invoice
          send screen already gives you. Each reminder can be edited here, and
          what you see is exactly what is sent. */}
      <Modal
        title={t('Preview reminders')}
        open={previewOpen}
        onCancel={sending ? undefined : () => setPreviewOpen(false)}
        onOk={sendReminders}
        okText={`${t('Send')} ${drafts.length} reminder${drafts.length === 1 ? '' : 's'}`}
        okButtonProps={{ icon: <MailOutlined />, loading: sending, disabled: !drafts.length }}
        cancelButtonProps={{ disabled: sending }}
        width={760}
        destroyOnClose
      >
        {previewSkipped.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={`${previewSkipped.length} selected invoice${previewSkipped.length === 1 ? '' : 's'} cannot be chased — no email address`}
            description={previewSkipped.map(x => `${x.invoiceNo} (${x.customer})`).join(', ')}
          />
        )}
        {drafts.length === 0 ? (
          <Alert type="info" showIcon message={t('Nothing to send — none of the selected invoices has an email address.')} />
        ) : (
          <>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={t('One email per invoice, each with the invoice PDF attached. You can edit any of them before sending.')}
            />
            <Collapse
              accordion
              defaultActiveKey={drafts[0]?.id}
              items={drafts.map(d => ({
                key: d.id,
                label: `${d.invoiceNo} — ${d.customer}  →  ${d.to}`,
                children: (
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <div>
                      <div style={{ marginBottom: 4 }}>{t('To')}</div>
                      <Input
                        value={d.to}
                        onChange={e => updateDraft(d.id, 'to', e.target.value)}
                        placeholder="customer@example.com (comma-separated for multiple)"
                      />
                    </div>
                    <div>
                      <div style={{ marginBottom: 4 }}>{t('Subject')}</div>
                      <Input value={d.subject} onChange={e => updateDraft(d.id, 'subject', e.target.value)} />
                    </div>
                    <div>
                      <div style={{ marginBottom: 4 }}>{t('Message')}</div>
                      <Input.TextArea
                        value={d.body}
                        onChange={e => updateDraft(d.id, 'body', e.target.value)}
                        autoSize={{ minRows: 8, maxRows: 18 }}
                      />
                    </div>
                  </Space>
                ),
              }))}
            />
          </>
        )}
      </Modal>

      <Modal
        title={t('Reminder results')}
        open={!!result}
        onCancel={() => setResult(null)}
        footer={[<Button key="c" type="primary" onClick={() => setResult(null)}>{t('Close')}</Button>]}
        width={640}
      >
        {result && (
          <>
            {result.sent.length > 0 && (
              <Alert type="success" showIcon style={{ marginBottom: 12 }}
                message={`Sent ${result.sent.length} reminder${result.sent.length > 1 ? 's' : ''}`} />
            )}
            {result.skipped.length > 0 && (
              <>
                <Alert type="warning" showIcon style={{ marginBottom: 8 }}
                  message={`Skipped ${result.skipped.length} — no email address on file`} />
                <Table size="small" pagination={false} rowKey="invoiceNo"
                  dataSource={result.skipped} style={{ marginBottom: 12 }}
                  columns={[
                    { title: t('Invoice'), dataIndex: 'invoiceNo', width: 160 },
                    { title: t('Customer'), dataIndex: 'customer' },
                  ]} />
              </>
            )}
            {result.failed.length > 0 && (
              <>
                <Alert type="error" showIcon style={{ marginBottom: 8 }}
                  message={`${result.failed.length} could not be sent`} />
                <Table size="small" pagination={false} rowKey="invoiceNo"
                  dataSource={result.failed}
                  columns={[
                    { title: t('Invoice'), dataIndex: 'invoiceNo', width: 160 },
                    { title: t('Reason'), dataIndex: 'error' },
                  ]} />
              </>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default ARChasing;
